// src/pages/VehicleOwnerSignIn.jsx
import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useUser } from "../context/UserContext";
import { ownerRequestOtpApi } from "../api/ownerPortalApi";

/* ───────── Inline icon helper ───────── */
const Icon = ({ d, size = 20, title, className }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    role={title ? "img" : "presentation"}
    aria-label={title}
    className={className}
  >
    {title ? <title>{title}</title> : null}
    <path d={d} fill="currentColor" />
  </svg>
);

const mdi = {
  steering:
    "M12 2A10 10 0 0 0 2 12C2 17.5 6.5 22 12 22S22 17.5 22 12A10 10 0 0 0 12 2M12 4A8 8 0 0 1 20 12C20 12.35 19.97 12.69 19.92 13H15.83C15.46 11.82 14.35 11 13.11 11H10.89C9.65 11 8.54 11.82 8.17 13H4.08C4.03 12.69 4 12.35 4 12A8 8 0 0 1 12 4M8.5 15A1.5 1.5 0 0 1 10 16.5A1.5 1.5 0 0 1 8.5 18A1.5 1.5 0 0 1 7 16.5A1.5 1.5 0 0 1 8.5 15M15.5 15A1.5 1.5 0 0 1 17 16.5A1.5 1.5 0 0 1 15.5 18A1.5 1.5 0 0 1 14 16.5A1.5 1.5 0 0 1 15.5 15M12 12.5A1.5 1.5 0 0 1 13.5 14A1.5 1.5 0 0 1 12 15.5A1.5 1.5 0 0 1 10.5 14A1.5 1.5 0 0 1 12 12.5Z",
  lockOutline:
    "M12 17A2 2 0 0 0 14 15A2 2 0 0 0 12 13A2 2 0 0 0 10 15A2 2 0 0 0 12 17M17 8H16V6A4 4 0 0 0 8 6V8H7A1 1 0 0 0 6 9V19A1 1 0 0 0 7 20H17A1 1 0 0 0 18 19V9A1 1 0 0 0 17 8M10 6A2 2 0 0 1 12 4A2 2 0 0 1 14 6V8H10Z",
  phoneOutline:
    "M6.62 10.79C8.06 13.62 10.38 15.94 13.21 17.38L15.41 15.18C15.69 14.9 16.08 14.8 16.45 14.9C17.65 15.3 18.93 15.5 20.25 15.5C20.8 15.5 21.25 15.95 21.25 16.5V20.25C21.25 20.8 20.8 21.25 20.25 21.25C10.19 21.25 2.75 13.81 2.75 3.75C2.75 3.2 3.2 2.75 3.75 2.75H7.5C8.05 2.75 8.5 3.2 8.5 3.75C8.5 5.07 8.7 6.35 9.1 7.55C9.2 7.92 9.1 8.31 8.82 8.59L6.62 10.79Z",
  keyOutline:
    "M7 14A2 2 0 0 1 9 12A2 2 0 0 1 11 14A2 2 0 0 1 9 16A2 2 0 0 1 7 14M12.65 10.65L11.5 11.8L13.5 13.8L12.09 15.21L10.09 13.21L8.94 14.36L10.94 16.36L9.53 17.77L5.76 14L7.17 12.59L8.32 13.74L9.47 12.59L7.47 10.59L10.06 8H15V10H12.65Z",
  eyeOutline:
    "M12 6C7 6 2.73 9.11 1 14C2.73 18.89 7 22 12 22S21.27 18.89 23 14C21.27 9.11 17 6 12 6M12 20C8.5 20 5.45 17.64 4 14C5.45 10.36 8.5 8 12 8S18.55 10.36 20 14C18.55 17.64 15.5 20 12 20M12 10A4 4 0 0 0 8 14A4 4 0 0 0 12 18A4 4 0 0 0 16 14A4 4 0 0 0 12 10Z",
  eyeOffOutline:
    "M2 5.27L3.28 4L20 20.72L18.73 22L15.82 19.09C14.64 19.69 13.36 20 12 20C7 20 2.73 16.89 1 12C1.69 10.17 2.78 8.57 4.17 7.27L2 5.27M12 8C15.5 8 18.55 10.36 20 14C19.67 14.83 19.24 15.59 18.73 16.27L17.26 14.8C17.41 14.55 17.54 14.28 17.64 14C16.2 10.36 13.16 8 12 8C11.66 8 11.33 8.05 11 8.14L9.39 6.53C10.23 6.19 11.1 6 12 6C17 6 21.27 9.11 23 14C22.45 15.43 21.62 16.72 20.58 17.82L19.12 16.36C19.71 15.66 20.22 14.86 20.63 14C19.18 10.36 16.14 8 12 8M12 10A4 4 0 0 1 16 14C16 14.9 15.8 15.77 15.47 16.61L13.86 15C13.95 14.67 14 14.34 14 14A2 2 0 0 0 12 12C11.66 12 11.33 12.05 11 12.14L9.39 10.53C10.23 10.19 11.1 10 12 10Z",
  alertCircleOutline:
    "M13 13H11V7H13M13 17H11V15H13M12 2A10 10 0 0 0 2 12A10 10 0 0 0 12 22A10 10 0 0 0 22 12A10 10 0 0 0 12 2Z",
  login: "M10 17L12 19L19 12L12 5L10 7L14 11H3V13H14L10 17Z",
};

function cleanPhone(p = "") {
  return String(p).replace(/\D/g, "").slice(0, 10);
}

export default function VehicleOwnerSignIn() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const { ownerSignIn } = useUser();

  const [mode, setMode] = useState("otp"); // "otp" | "password"
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const phoneRef = useRef(null);
  const otpRef = useRef(null);

  // keep your behavior: always go to owner dashboard
  const _redirectParam = params.get("redirect");
  const effectiveRedirect = "/owner/dashboard";

  const normalizedPhone = useMemo(() => cleanPhone(phoneNumber), [phoneNumber]);
  const canSendOtp = useMemo(() => normalizedPhone.length === 10, [normalizedPhone]);
  const canOtpLogin = useMemo(
    () => canSendOtp && (otp || "").trim().length >= 4,
    [canSendOtp, otp]
  );
  const canPwdLogin = useMemo(
    () => normalizedPhone.length === 10 && !!(password || "").trim(),
    [normalizedPhone, password]
  );

  const resetErrorsOnly = () => setError("");

  const handleRequestOtp = async () => {
    if (!canSendOtp || busy) return;
    setBusy(true);
    setError("");
    try {
      await ownerRequestOtpApi({ phoneNumber: normalizedPhone });
      setOtpSent(true);
      setTimeout(() => otpRef.current?.focus?.(), 50);
    } catch (err) {
      console.error("Owner OTP request failed", err);
      setError("Could not send OTP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleOtpSignIn = async (e) => {
    e.preventDefault();
    if (!canOtpLogin || busy) return;
    setBusy(true);
    setError("");

    try {
      const res = await ownerSignIn({
        mode: "otp",
        phoneNumber: normalizedPhone,
        otp: (otp || "").trim(),
      });

      if (res?.isNewOwner) {
        const url = `/vehicle-owner/vehicle-owner-signup?via=signin&phoneNumber=${encodeURIComponent(
          normalizedPhone
        )}&redirect=${encodeURIComponent(effectiveRedirect)}`;
        navigate(url, { replace: true });
        return;
      }

      navigate(effectiveRedirect, { replace: true });
    } catch (err) {
      console.error("Owner OTP sign in failed", err);
      setError("Invalid or expired OTP. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordSignIn = async (e) => {
    e.preventDefault();
    if (!canPwdLogin || busy) return;
    setBusy(true);
    setError("");

    try {
      await ownerSignIn({
        mode: "password",
        phoneNumber: normalizedPhone,
        password: (password || "").trim(),
      });
      navigate(effectiveRedirect, { replace: true });
    } catch (err) {
      console.error("Owner password sign in failed", err);
      setError("Invalid credentials. Use OTP if you haven't set a password.");
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = mode === "otp" ? handleOtpSignIn : handlePasswordSignIn;

  const tabBtn = (active) =>
    [
      "flex-1 rounded-xl py-2 text-xs font-semibold transition",
      active
        ? "bg-white text-slate-900 shadow-sm border border-slate-200"
        : "text-slate-500 hover:text-slate-700",
    ].join(" ");

  // Clean, sharp inputs without “glow”
  const inputClass =
    "w-full rounded-xl border border-slate-300 bg-white px-10 py-2.5 text-[15px] outline-none " +
    "focus:border-slate-400 focus:ring-0 focus:outline-none " +
    "transition-colors";

  const iconWrapClass =
    "absolute left-3 top-1/2 -translate-y-1/2 text-slate-400";

  const primaryBtn =
    "w-full inline-flex items-center justify-center rounded-xl bg-black px-5 py-2.5 text-white font-semibold " +
    "hover:bg-slate-900 active:bg-black disabled:opacity-60 disabled:cursor-not-allowed transition";

  const subtleBtn =
    "font-semibold text-pink-600 hover:text-pink-700 active:text-pink-800 disabled:text-slate-300 disabled:cursor-not-allowed transition";

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />

      <main className="mx-auto max-w-[520px] px-4 sm:px-6 xl:px-8 py-10 sm:py-12">
        {/* Minimal premium page feel (not noisy) */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-[0_8px_30px_-18px_rgba(15,23,42,0.35)] p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white border border-slate-200">
              <Icon
                d={mdi.steering}
                size={20}
                className="text-slate-700"
                title="Vehicle owner sign in"
              />
            </div>
            <div className="min-w-0">
              <h1 className="text-[22px] sm:text-2xl font-bold leading-tight">
                Vehicle owner sign in
              </h1>
              <p className="mt-1 text-slate-600 text-sm">
                OTP is fastest. Password is ideal for daily fleet operations.
              </p>
            </div>
          </div>

          {/* Mode toggle */}
          <div className="mt-5 flex rounded-2xl border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => {
                setMode("otp");
                resetErrorsOnly();
                setPassword("");
                setShowPwd(false);
                setTimeout(() => phoneRef.current?.focus?.(), 50);
              }}
              className={tabBtn(mode === "otp")}
            >
              OTP login
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("password");
                resetErrorsOnly();
                setOtp("");
                setOtpSent(false);
                setTimeout(() => phoneRef.current?.focus?.(), 50);
              }}
              className={tabBtn(mode === "password")}
            >
              Password login
            </button>
          </div>

          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <Icon d={mdi.alertCircleOutline} size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Form */}
          <form className="mt-6 space-y-4" onSubmit={onSubmit}>
            {/* Phone */}
            <label className="block">
              <span className="text-sm text-slate-700 font-medium">
                Phone number
              </span>
              <div className="mt-1.5 relative">
                <span className={iconWrapClass}>
                  <Icon d={mdi.phoneOutline} />
                </span>
                <input
                  ref={phoneRef}
                  type="tel"
                  inputMode="numeric"
                  placeholder="10-digit mobile number"
                  value={phoneNumber}
                  onChange={(e) => {
                    const v = cleanPhone(e.target.value);
                    setPhoneNumber(v);
                    // keep OTP sent state meaningful: if phone changes after OTP sent, reset it
                    if (otpSent) setOtpSent(false);
                    if (otp) setOtp("");
                  }}
                  required
                  className={inputClass}
                />
              </div>
            </label>

            {/* OTP mode */}
            {mode === "otp" && (
              <>
                <div className="flex items-center justify-between text-[11px] mt-1">
                  <span className="text-slate-500">
                    We&apos;ll send an OTP to this number.
                  </span>
                  <button
                    type="button"
                    onClick={handleRequestOtp}
                    disabled={busy || !canSendOtp}
                    className={subtleBtn}
                  >
                    {busy ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
                  </button>
                </div>

                <label className="block">
                  <span className="text-sm text-slate-700 font-medium">OTP</span>
                  <div className="mt-1.5 relative">
                    <span className={iconWrapClass}>
                      <Icon d={mdi.keyOutline} />
                    </span>
                    <input
                      ref={otpRef}
                      type="tel"
                      inputMode="numeric"
                      placeholder="4-6 digit OTP"
                      value={otp}
                      onChange={(e) =>
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                      }
                      className={inputClass}
                    />
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={busy || !canOtpLogin}
                  className={primaryBtn}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon d={mdi.login} size={18} />
                    <span>{busy ? "Verifying..." : "Verify & sign in"}</span>
                  </span>
                </button>
              </>
            )}

            {/* Password mode */}
            {mode === "password" && (
              <>
                <label className="block">
                  <span className="text-sm text-slate-700 font-medium">
                    Password
                  </span>
                  <div className="mt-1.5 relative">
                    <span className={iconWrapClass}>
                      <Icon d={mdi.lockOutline} />
                    </span>
                    <input
                      type={showPwd ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-500 hover:text-slate-700 transition"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                    >
                      <Icon
                        d={showPwd ? mdi.eyeOffOutline : mdi.eyeOutline}
                        size={20}
                      />
                    </button>
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={busy || !canPwdLogin}
                  className={primaryBtn}
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon d={mdi.login} size={18} />
                    <span>{busy ? "Signing in..." : "Sign in"}</span>
                  </span>
                </button>

                <p className="mt-2 text-xs text-slate-500">
                  If you haven&apos;t set a password yet, use OTP login and then
                  set a password from your owner security page.
                </p>
              </>
            )}
          </form>

          {/* Owner signup link */}
          <div className="mt-6 text-sm text-slate-700">
            New vehicle owner?{" "}
            <Link
              to={`/vehicle-owner/vehicle-owner-signup?redirect=${encodeURIComponent(
                effectiveRedirect
              )}`}
              className="font-semibold text-slate-900 underline underline-offset-4 hover:no-underline"
            >
              Register your fleet
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}